import { type Attachment, AttachmentsDropzone, Form } from '@faceup/ui'
import { Alert } from '@faceup/ui-base'
import { DEFAULT_LANGUAGE, Plan } from '@faceup/utils'
import { useContext, useMemo } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../__generated__'
import { LanguageContext } from '../../Contexts/LanguageContext'
import useConfigForProject from '../../hooks/useConfigForProject'
import { sharedMessages } from '../../Shared/translations'
import { defineMessages, FormattedMessage, useIntl } from '../../TypedIntl'

const messages = defineMessages({
  attachmentsLabel: 'Shared.report.attachments',
  dropzoneDrop: 'FollowUp.report.dropzone.drop',
  dropzoneOpen: 'FollowUp.report.dropzone.open',
  dropzoneLimit: 'FollowUp.report.dropzone.limit',
  addAttachment: 'Shared.report.attachments.add',
  alertMessage: 'FollowUp.report.attachments.alert.message',
  alertLink: 'FollowUp.report.attachments.alert.link',
})

const fragments = {
  AttachmentsDropzoneFormItem_institution: graphql(`
    fragment AttachmentsDropzoneFormItem_institution on PublicCompany {
      type
      billing {
        plan
      }
    }
  `),
}

type AttachmentsDropzoneFormItemProps = {
  onSetAttachments: (attachments: Attachment[]) => void
  institution?: FragmentType<typeof fragments.AttachmentsDropzoneFormItem_institution> | null
  attachments: Attachment[]
}

export const AttachmentsDropzoneFormItem = ({
  attachments,
  onSetAttachments,
  institution: _institution,
}: AttachmentsDropzoneFormItemProps) => {
  const institution = getFragmentData(
    fragments.AttachmentsDropzoneFormItem_institution,
    _institution
  )
  const { formatMessage } = useIntl()

  const { getSupportUrlByLanguage } = useConfigForProject()

  const { language } = useContext(LanguageContext)

  // TODO: https://gitlab.com/faceup-nntb/monorepo/-/merge_requests/694#note_1132857869
  // create  and use useTier hook instead of this
  const canUseDropzone = useMemo(
    () =>
      Boolean(institution?.billing?.plan) &&
      [Plan.Basic, Plan.Standard, Plan.Premium].includes(institution?.billing?.plan ?? Plan.Basic),
    [institution?.billing?.plan]
  )

  if (!canUseDropzone) {
    return null
  }

  return (
    <Form.Item
      label={<FormattedMessage {...messages.attachmentsLabel} />}
      formItemProps={{
        'data-test': 'create-company-report-form-attachment-dropzone',
      }}
    >
      <AttachmentsDropzone
        attachments={attachments}
        setAttachments={files => {
          onSetAttachments(files)
        }}
        translations={{
          tooManyFiles: formatMessage(sharedMessages.attachmentsTooManyFiles),
          tooLargeFile: formatMessage(sharedMessages.attachmentsTooLargeFile),
          metadataRemoved: formatMessage(sharedMessages.metadataRemoved),
          metadataUnableToRemove: formatMessage(sharedMessages.metadataUnableToRemove),
          unsupportedMimetype: formatMessage(sharedMessages.unsupportedMimetype),
          delete: formatMessage(sharedMessages.delete),
          dropzoneDrop: formatMessage(messages.dropzoneDrop),
          dropzoneLimits: formatMessage(messages.dropzoneLimit),
          dropzoneOpen: formatMessage(messages.dropzoneOpen),
          add: formatMessage(messages.addAttachment),
        }}
      />
      {attachments.length > 0 && (
        <Alert
          className='mt-4'
          type='info'
          closable
          message={
            <div className='p-1'>
              <FormattedMessage {...messages.alertMessage} />{' '}
              <a
                className='underline'
                href={getSupportUrlByLanguage(language ?? DEFAULT_LANGUAGE)}
                target='_blank'
                rel='noopener noreferrer'
              >
                <FormattedMessage {...messages.alertLink} />
              </a>
            </div>
          }
        />
      )}
    </Form.Item>
  )
}
